import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import logo from 'assets/img/logos/hy-logo.png';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const EMPTY = {
  full_name: '',
  address: '',
  date_of_birth: '',
  email: '',
  phone: '',
  countries_visited: [],
  departure_date: '',
  return_date: '',
  has_allergies: 'no',
  allergies_details: '',
  has_conditions: 'no',
  conditions_details: '',
  takes_medication: 'no',
  medication_details: '',
  has_further_info: 'no',
  further_info: '',
  gp_name: '',
  gp_phone: '',
  gp_address: '',
  ec1_name: '',
  ec1_phone: '',
  ec1_address: '',
  ec1_relationship: '',
  ec2_name: '',
  ec2_phone: '',
  ec2_address: '',
  ec2_relationship: '',
  consent_confirmed: false,
  signature_name: '',
  website: '' // honeypot
};

// Field components live at module level: defining them inside the page would
// create a new component type on every keystroke, remounting the input and
// dropping focus after one character.
const Section = ({ title, children }) => (
  <Card className="mb-3">
    <Card.Header className="bg-light">
      <h5 className="mb-0">{title}</h5>
    </Card.Header>
    <Card.Body>{children}</Card.Body>
  </Card>
);
Section.propTypes = { title: PropTypes.string, children: PropTypes.node };

const Text = ({
  name,
  label,
  value,
  onChange,
  required,
  type = 'text',
  as,
  rows,
  md = 6
}) => (
  <Col md={md}>
    <Form.Group className="mb-3">
      <Form.Label>
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      <Form.Control
        type={type}
        as={as}
        rows={rows}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
      />
    </Form.Group>
  </Col>
);
Text.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  type: PropTypes.string,
  as: PropTypes.string,
  rows: PropTypes.number,
  md: PropTypes.number
};

const YesNo = ({ name, label, value, onChange }) => (
  <Form.Group className="mb-2">
    <Form.Label className="fw-medium">{label}</Form.Label>
    <div className="d-flex gap-4">
      {['no', 'yes'].map(opt => (
        <Form.Check
          key={opt}
          inline
          type="radio"
          id={`${name}-${opt}`}
          name={name}
          label={opt === 'yes' ? 'Yes' : 'No'}
          value={opt}
          checked={value === opt}
          onChange={onChange}
        />
      ))}
    </div>
  </Form.Group>
);
YesNo.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func
};

// Branch countries volunteers travel to; multi-select for multi-country trips
const TRIP_COUNTRIES = ['Kenya', 'Gambia', 'Somalia', 'Sudan', 'Yemen'].map(
  c => ({
    label: c,
    value: c
  })
);

const CountrySelect = ({ value, onChange }) => (
  <Col md={12}>
    <Form.Group className="mb-3">
      <Form.Label>
        Country(ies) to be visited <span className="text-danger">*</span>
      </Form.Label>
      <ReactSelect
        isMulti
        options={TRIP_COUNTRIES}
        value={TRIP_COUNTRIES.filter(o => value.includes(o.value))}
        onChange={opts => onChange((opts || []).map(o => o.value))}
        placeholder="Select one or more countries..."
        classNamePrefix="react-select"
        closeMenuOnSelect={false}
      />
    </Form.Group>
  </Col>
);
CountrySelect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired
};

/**
 * Public (no login) version of the "Health Information for International
 * Travel" volunteer form. Submits via the submit-medical-form edge function;
 * the browser never touches the table directly.
 */
const PublicMedicalForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const set = (name, value) => setValues(v => ({ ...v, [name]: value }));
  const onChange = e => {
    const { name, type, checked, value } = e.target;
    set(name, type === 'checkbox' ? checked : value);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!values.countries_visited.length) {
      setError('Please select at least one country to be visited.');
      return;
    }
    if (!values.consent_confirmed) {
      setError('Please confirm the consent statement before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        countries_visited: values.countries_visited.join(', '),
        has_allergies: values.has_allergies === 'yes',
        has_conditions: values.has_conditions === 'yes',
        takes_medication: values.takes_medication === 'yes',
        has_further_info: values.has_further_info === 'yes'
      };
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/submit-medical-form`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: ANON_KEY,
            Authorization: `Bearer ${ANON_KEY}`
          },
          body: JSON.stringify(payload)
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
      } else {
        setDone(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {
      setError('Could not reach the server. Please check your connection.');
    }
    setSubmitting(false);
  };

  return (
    <Container className="py-4 py-md-5" style={{ maxWidth: 900 }}>
      <div className="text-center mb-4">
        <img src={logo} alt="Help Yateem" style={{ height: 56 }} />
        <h3 className="mt-3 mb-1">
          Health Information for International Travel
        </h3>
        <p className="text-600 mb-0">
          This form must be completed in order for volunteers to take part in an
          international trip with Help Yateem. Please provide detailed
          information relating to any illnesses, allergies and/or medication in
          case of a medical emergency.
        </p>
      </div>

      {done ? (
        <Card className="text-center py-5">
          <Card.Body>
            <FontAwesomeIcon
              icon="check-circle"
              className="text-success fs-4 mb-3"
            />
            <h4>Thank you, your form has been received</h4>
            <p className="text-600 mb-0">
              The Help Yateem team will be in touch if anything further is
              needed. This information is confidential and will only be
              disclosed on a need-to-know basis.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Form onSubmit={handleSubmit}>
          {error && <Alert variant="danger">{error}</Alert>}

          <Section title="Personal details">
            <Row>
              <Text
                name="full_name"
                value={values.full_name}
                onChange={onChange}
                label="Full name"
                required
              />
              <Text
                name="date_of_birth"
                value={values.date_of_birth}
                onChange={onChange}
                label="Date of birth"
                type="date"
                required
              />
              <Text
                name="address"
                value={values.address}
                onChange={onChange}
                label="Address"
                required
                as="textarea"
                rows={2}
                md={12}
              />
              <Text
                name="email"
                value={values.email}
                onChange={onChange}
                label="Email"
                type="email"
              />
              <Text
                name="phone"
                value={values.phone}
                onChange={onChange}
                label="Telephone"
                type="tel"
              />
              <CountrySelect
                value={values.countries_visited}
                onChange={v => set('countries_visited', v)}
              />
              <Text
                name="departure_date"
                value={values.departure_date}
                onChange={onChange}
                label="Departure date"
                type="date"
                required
              />
              <Text
                name="return_date"
                value={values.return_date}
                onChange={onChange}
                label="Return date"
                type="date"
                required
              />
            </Row>
          </Section>

          <Section title="Health / medical information">
            <YesNo
              name="has_allergies"
              value={values.has_allergies}
              onChange={onChange}
              label="Do you have any allergies?"
            />
            {values.has_allergies === 'yes' && (
              <Row>
                <Text
                  name="allergies_details"
                  value={values.allergies_details}
                  onChange={onChange}
                  label="Please provide full details"
                  required
                  as="textarea"
                  rows={3}
                  md={12}
                />
              </Row>
            )}
            <YesNo
              name="has_conditions"
              value={values.has_conditions}
              onChange={onChange}
              label="Do you have any existing medical conditions?"
            />
            {values.has_conditions === 'yes' && (
              <Row>
                <Text
                  name="conditions_details"
                  value={values.conditions_details}
                  onChange={onChange}
                  label="Please provide full details"
                  required
                  as="textarea"
                  rows={3}
                  md={12}
                />
              </Row>
            )}
            <YesNo
              name="takes_medication"
              value={values.takes_medication}
              onChange={onChange}
              label="Are you currently taking any medication?"
            />
            {values.takes_medication === 'yes' && (
              <Row>
                <Text
                  name="medication_details"
                  value={values.medication_details}
                  onChange={onChange}
                  label="Please provide details including reason for its use"
                  required
                  as="textarea"
                  rows={3}
                  md={12}
                />
              </Row>
            )}
            <YesNo
              name="has_further_info"
              value={values.has_further_info}
              onChange={onChange}
              label="Is there any further information that Help Yateem should have regarding your health and well-being?"
            />
            {values.has_further_info === 'yes' && (
              <Row>
                <Text
                  name="further_info"
                  value={values.further_info}
                  onChange={onChange}
                  label="Please provide details"
                  required
                  as="textarea"
                  rows={3}
                  md={12}
                />
              </Row>
            )}
          </Section>

          <Section title="Doctor contact information">
            <Row>
              <Text
                name="gp_name"
                value={values.gp_name}
                onChange={onChange}
                label="Name"
                required
              />
              <Text
                name="gp_phone"
                value={values.gp_phone}
                onChange={onChange}
                label="Telephone number"
                type="tel"
                required
              />
              <Text
                name="gp_address"
                value={values.gp_address}
                onChange={onChange}
                label="Address"
                required
                as="textarea"
                rows={2}
                md={12}
              />
            </Row>
          </Section>

          <Section title="Emergency contacts">
            <p className="text-600">
              Please provide details of 2 people who can be contacted at all
              times during the trip.
            </p>
            {[1, 2].map(n => (
              <div key={n} className={n === 2 ? 'pt-3 border-top' : ''}>
                <h6 className="text-700">Contact {n}</h6>
                <Row>
                  <Text
                    name={`ec${n}_name`}
                    value={values[`ec${n}_name`]}
                    onChange={onChange}
                    label="Name"
                    required
                  />
                  <Text
                    name={`ec${n}_phone`}
                    value={values[`ec${n}_phone`]}
                    onChange={onChange}
                    label="Telephone"
                    type="tel"
                    required
                  />
                  <Text
                    name={`ec${n}_address`}
                    value={values[`ec${n}_address`]}
                    onChange={onChange}
                    label="Address"
                    as="textarea"
                    rows={2}
                    md={12}
                  />
                  <Text
                    name={`ec${n}_relationship`}
                    value={values[`ec${n}_relationship`]}
                    onChange={onChange}
                    label="Relationship to you"
                    required
                    md={12}
                  />
                </Row>
              </div>
            ))}
          </Section>

          <Section title="Consent">
            <Row>
              <Text
                name="signature_name"
                value={values.signature_name}
                onChange={onChange}
                label="Your full name (as signature)"
                required
                md={12}
              />
            </Row>
            <Form.Check
              type="checkbox"
              id="consent"
              name="consent_confirmed"
              checked={values.consent_confirmed}
              onChange={onChange}
              label={`I confirm that I, ${
                values.signature_name || '________'
              }, am fit to take part in this trip, and that the above information is correct at the time of travel.`}
            />
            <p className="text-600 fs--1 mt-3 mb-0">
              This information is confidential and will only be disclosed on a
              need-to-know basis.
            </p>
          </Section>

          {/* Honeypot - hidden from people, bots fill it */}
          <div
            style={{ position: 'absolute', left: '-9999px' }}
            aria-hidden="true"
          >
            <label>
              Website
              <input name="website" tabIndex={-1} autoComplete="off" />
            </label>
          </div>

          <div className="d-grid">
            <Button type="submit" size="lg" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit form'}
            </Button>
          </div>
          <p className="text-center text-500 fs--1 mt-3">
            Help Yateem · Hyat Hub, Suite 13, Durning Hall, Earlham Grove,
            London, E7 9AB · info@helpyateem.org
          </p>
        </Form>
      )}
    </Container>
  );
};

export default PublicMedicalForm;
